import React, { useState, useEffect } from "react";
import useMobileBreakpoint from '@src/hooks/useMobileBreakpoint';

interface Variant {
  id: string;
  title: string;
  available: boolean;
  price?: number;
  option1?: string;
  option2?: string;
  option3?: string;
}

interface Product {
  id: string;
  handle?: string;
  variants?: Variant[];
  options?: Array<{
    name: string;
    values: string[];
  }>;
}

interface ComponentProps {
  block_id?: string;
  block_settings?: any;
  current_product?: Product;
  current_product_form_id?: string;
  [key: string]: any;
}

interface ColourOption {
  name: string;
  value: string;
  url: string;
  available: boolean;
}

interface SelectedColour {
  current_colour: string;
  current_swatch: string;
  swatch_type: string;
}

export const StickyColourSelector = (props: ComponentProps) => {
  const [selectedColour, setSelectedColour] = useState<SelectedColour | null>(null);
  const [isActive, setIsActive] = useState(false);

  const isMobile = useMobileBreakpoint(props.settings.globals.responsive_breakpoints?.mobile_max || 767);

  // Extract settings from block_settings with defaults
  const {
    option,
    selector_style,
    selector_style_mobile,
    button_text = "Color",
    placeholder_text = "Select Color",
    color_option_source = "variants",
    color_option_name = "Color",
    display_style = "swatches",
    swatch_size = 32,
    swatch_border_radius = 4,
    show_color_names = true,
    swatch_gap = 8,
    button_text_color = "#000000",
    border_radius = 6,
  } = props.settings.block_settings;

  const product = props.settings.product;
  const product_form_id = props.current_product_form_id;
  const block_id = props.block_id?.replace(/_/g, '').toLowerCase() || 'color-selector';

  // Extract color options from props.settings.swatches
  const colourOptions: ColourOption[] = React.useMemo(() => {
    if (props.settings?.swatches.swatches) {
      return props.settings.swatches.swatches.map((swatch: any) => ({
        name: swatch.colour_name.includes('-') ? 
          swatch.colour_name.substring(swatch.colour_name.lastIndexOf('-') + 1).trim() : 
          swatch.colour_name || 
          (swatch.name.includes('-') ? 
            swatch.name.substring(swatch.name.lastIndexOf('-') + 1).trim() : 
            swatch.name) || '',
        value: swatch.color || swatch.colour || swatch.value || '',
        url: swatch.url || '',
        available: swatch.available !== false
      }));
    }
    return [];
  }, [props.settings]);

  // Set initial color from props.settings.swatches
  useEffect(() => {
    if (props.settings?.swatches) {
      const swatches = props.settings.swatches;
      // Use current_colour as the initial selected color
      if (swatches.current_colour && !selectedColour) {
        setSelectedColour({
          current_colour: swatches.current_colour.includes('-') ? 
            swatches.current_colour.substring(swatches.current_colour.lastIndexOf('-') + 1).trim() : 
            swatches.current_colour,
          current_swatch: swatches.current_swatch || '',
          swatch_type: swatches.swatch_type || 'color'
        });
      }
    }
  }, [props.settings, selectedColour]);


  // Get color value for CSS based on swatch_type
  const getColourValue = (colourName: string): string => {
    const swatches = props.settings?.swatches;
    
    // If swatch_type is image and we have current_swatch, use it as background image
    if (swatches?.swatch_type === 'image' && swatches?.current_swatch) {
      return `url(${swatches.current_swatch})`;
    }
    
    // Otherwise use color mapping for background color
    const colourMap: { [key: string]: string } = {
      'black': '#000000',
      'white': '#ffffff',
      'red': '#ef4444',
      'blue': '#3b82f6',
      'green': '#10b981',
      'yellow': '#f59e0b',
      'purple': '#8b5cf6',
      'pink': '#ec4899',
      'gray': '#6b7280',
      'grey': '#6b7280',
      'brown': '#a3834e',
      'orange': '#f97316',
      'navy': '#1e3a8a',
      'slate': '#64748b'
    };

    const normalizedName = colourName.toLowerCase().replace(/\s+/g, '');
    return colourMap[normalizedName] || `var(--color-${normalizedName}, #cccccc)`;
  };

  const handleButtonClick = () => {
    const swatches = props.settings?.swatches;
    const newActiveState = !isActive;
    setIsActive(newActiveState);
    
    // Determine panel title based on mode
    const getPanelTitle = () => {
      if (isMobile) {
        // Mobile: individual panel, use custom title or default
        return option || 'Color';
      } else {
        // Desktop: combined panel, don't set panelTitle so it uses defaults in render functions
        return undefined;
      }
    };
    
    // Dispatch event to show/hide selection panel
    window.dispatchEvent(new CustomEvent('stickybar:togglePanel', {
      detail: { 
        type: isMobile ? 'colour' : 'combined',
        isActive: newActiveState,
        blockId: block_id,
        panelTitle: getPanelTitle(),
        swatchType: swatches?.swatch_type || 'color',
        colourOptions: colourOptions,
        sizeOptions: isMobile ? [] : (window as any).stickySizeOptions || [], // Get size options from global if available
        selectedColourOption: selectedColour?.current_colour,
        selectedSizeOption: isMobile ? null : (window as any).stickySelectedSize || null,
        displayStyle: display_style,
        swatchSize: swatch_size,
        swatchBorderRadius: swatch_border_radius,
        showColorNames: show_color_names,
        swatchGap: swatch_gap,
        getColourValue,
        productId: product?.id || ''
      }
    }));
  };

  // Listen for panel close events and combined panel state
  useEffect(() => {
    const handlePanelClose = () => {
      setIsActive(false);
    };

    const handlePanelToggle = (event: CustomEvent) => {
      const data = event.detail;
      
      if (!isMobile && data.type === 'combined') {
        // On desktop, both selectors should show active state when combined panel is open
        setIsActive(data.isActive);
      } else if (isMobile) {
        // On mobile, if another selector opens, close this one
        if (data.type === 'size' && data.blockId !== block_id && data.isActive) {
          setIsActive(false);
        }
      }
    };

    window.addEventListener('stickybar:panelClosed', handlePanelClose);
    window.addEventListener('stickybar:togglePanel', handlePanelToggle as EventListener);
    
    return () => {
      window.removeEventListener('stickybar:panelClosed', handlePanelClose);
      window.removeEventListener('stickybar:togglePanel', handlePanelToggle as EventListener);
    };
  }, [isMobile, block_id]);

  // Store color options and handlers globally for cross-component access
  useEffect(() => {
    (window as any).stickyColorOptions = colourOptions;
    (window as any).stickySelectedColor = selectedColour?.current_colour;
  }, [colourOptions, selectedColour]);

  const styles = {
    container: {
      position: 'relative' as const,
      width: '100%'
    },
    button: {
      width: '100%',
      padding: `8px 12px`,
      backgroundColor: '#FFFFFF',
      borderRadius: `${border_radius}px`,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between' as const,
      alignItems: 'center',
      fontSize: `18px`,
      color: '#000000',
      transition: 'all 0.2s ease',
      outline: 'none',
      textAlign: 'left' as const,
      minHeight: '44px'
    },
    buttonContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: 1,
      minWidth: 0
    },
    colorSwatch: {
      width: `20px`,
      height: `20px`,
      borderRadius: `2px`,
      border: '1px solid black',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative' as const
    },
    buttonText: {
      fontWeight: '500',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    dropdownIcon: {
      width: '16px',
      height: '16px',
      marginLeft: '8px',
      flexShrink: 0,
      transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }
  };

  if (colourOptions.length === 0) {
    return null; // Don't render if no color options available
  }

  return (
    <div 
      className={`sticky-color-selector-${block_id}`}
      style={styles.container}
      data-block-id={block_id}
    >
      <button
        type="button"
        style={styles.button}
        onClick={handleButtonClick}
        aria-expanded={isActive}
        aria-haspopup="true"
      >
        <div style={styles.buttonContent}>
          {selectedColour && (
            <div 
              style={{
                ...styles.colorSwatch,
                ...(selectedColour.swatch_type === 'image' && selectedColour.current_swatch 
                  ? {
                      backgroundImage: `url(${selectedColour.current_swatch})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }
                  : {
                      backgroundColor: selectedColour.current_swatch
                    }
                )
              }}
            />
          )}
          <span className={`${isMobile ? `${selector_style_mobile}` : `${selector_style}`}`}>
            {selectedColour?.current_colour || `${button_text}: ${placeholder_text}`}
          </span>
        </div>
        <div className="flex flex-row no-wrap">
          {!isMobile && colourOptions.length > 1 &&
            <p className="text-xs text-black/50">
              +{colourOptions.length} Colours
            </p>
          }
          <svg 
            style={styles.dropdownIcon}
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </div>
      </button>
    </div>
  );
};
